// The React-in-legacy-module bridge (Ben's Phase 04 coexistence model).
//
// The seam: refreshModuleBody(mod) (main.js's until 05b, now src/canvas/shell-actions.ts)
// looks up modules.json's `renderer` name and calls its mount into the module's `body`,
// through the component registry (component-registry.ts, 05j-1 — until then it called
// window[renderer], and a registration here overwrote the legacy renderer on window). Since 05b the module shell around `body` is React too
// (src/canvas/PlacedModule.tsx), but each body keeps its own root. The one thing that
// ever writes into a mounted body from outside is the merge's charging screen, and it goes
// through `showInsteadOfWidget` below (05g-2).
//
// Instance-state contract (undo/preset fidelity): a converted module keeps its own
// state on the module element as `_`-prefixed properties, exactly as the legacy
// renderers do (mod._leagueTableCompetition, …). main.js's saveState() still
// captures them generically (Phase 03's captureCustomState), so undo/preset keep
// working with no store wiring — the React state is just a render mirror.
//
// A restore (Undo, presets, saved dashboards, "All", side-panel tiles like "Attacking
// Rank") puts that state on the element BEFORE the module's first render since 05i-1
// (createModule's `custom`) — main.js rendered first and then again with the state. Other
// code still changes a module's `_`-state and then refreshes it (a comparison chip's ✕, the
// similarity overlay, a player change, the top bar's Refresh): see `rebuildOnRefresh`.

import { createElement, type ComponentType } from "react";
import { createRoot, type Root } from "react-dom/client";
import { flushSync } from "react-dom";
import { canvasApi } from "../canvas/shell-actions.js";
import { registerRenderer } from "./component-registry.js";
import { LIVE } from "../live/flag.js";
import { LiveBoundary } from "../live/LiveBoundary.js";
import { readSnapshot, snapshotPlayersOrNone } from "../data/snapshot.js";
import { isSnapshotPlayer, playerNameOf, type PlayerId } from "../data/players.js";
import { pickedPlayer } from "../live/picked-players.js";
import { LiveNotInDemoData } from "../live/LoadState.js";

export interface ProtoModuleProps {
  /** The .placed-module element (owns the header + `_`-state; managed by main.js). */
  mod: HTMLElement;
  /** The .module-body element React renders into. */
  body: HTMLElement;
  /** The module's player — her id (its .player-select value), or null. What a widget
   *  stores and passes on (ids everywhere, ids-1). */
  playerId: PlayerId | null;
  /** The same player's snapshot key (full name), worked out from `playerId` — for showing
   *  and for the snapshot lookups still keyed by name (fork A1: the snapshot goes away with
   *  Track 2). Never store it or pass it on: use `playerId`. Null for a player the snapshot
   *  lacks, in a widget registered with `livePlayers` (?live=1 only). */
  playerName: string | null;
  /** The modules.json config for this title (loose — most renderers ignore it). */
  modCfg: unknown;
}

// Keyed by the .module-body element a root renders into. A Map (not WeakMap) so the
// removal observer can look roots up; entries are deleted on unmount.
const roots = new Map<HTMLElement, Root>();

// How many times each body has been rendered — the React `key` for modules registered with
// `rebuildOnRefresh`, so every refreshModuleBody rebuilds them from their `_`-state.
const refreshCounts = new Map<HTMLElement, number>();

// The merge's charging screen (05g-2, fork 3A): React's own nodes, taken out of a body
// while the ⚡ screen stands in for them. The root stays mounted meanwhile, so the widget's
// portals outside the body (its season picker in the strip) stay on screen through the
// shake, as they always did. The nodes go back in before the root is touched again (its
// next render or its unmount), so React never works on a body it doesn't recognise.
// This replaces the old guard that spotted a body clobbered behind React's back and
// patched it up afterwards.
const swappedOut = new Map<HTMLElement, ChildNode[]>();

/** Show `html` in a widget's body in place of the widget, until its next render (a merge's
 *  charging screen). Same DOM as main.js's `body.innerHTML = html`. */
export function showInsteadOfWidget(body: HTMLElement, html: string): void {
  // A second call before the next render keeps the first set: those are React's nodes.
  if (roots.has(body) && !swappedOut.has(body)) swappedOut.set(body, Array.from(body.childNodes));
  body.innerHTML = html;
}

function putWidgetBack(body: HTMLElement): void {
  const nodes = swappedOut.get(body);
  if (!nodes) return;
  swappedOut.delete(body);
  body.replaceChildren(...nodes);
}

// Unmount a root. Put its nodes back first if the charging screen holds the body: React's
// unmount removes its own nodes from `body`, and it has to find them there. The unmount
// also runs the tree's effect cleanups, which remove the component's portals (the
// SubControlsSlot season slot, the PopulationReadout sibling) that live outside the body.
function teardownRoot(body: HTMLElement, root: Root): void {
  putWidgetBack(body);
  roots.delete(body);
  refreshCounts.delete(body);
  root.unmount();
}

/** The snapshot name for a module's player id. On the snapshot site an id it doesn't have
 *  can't come from its picker, so it's a bug — thrown, not shown as some other player.
 *  (With ?live=1, `mount` catches such a player first: see `notInSnapshot`.) */
function snapshotNameOf(playerId: PlayerId | null): string | null {
  if (playerId === null) return null;
  const name = playerNameOf(readSnapshot().players, playerId);
  if (name === null) throw new Error(`Player id ${playerId} isn't in the snapshot`);
  return name;
}

/**
 * With ?live=1 a pick can be anyone in the real DB (ids-3). A widget still on the snapshot
 * can't show a player the snapshot doesn't have, so it says so (fork 1B) — in this one place,
 * not in each widget, and never by quietly showing another player instead. Her name comes
 * from the pick itself; a player nobody picked would be a bug, so it throws. Null = show the
 * widget (always, on the snapshot site).
 */
function notInSnapshot(playerId: PlayerId | null): string | null {
  if (!LIVE || playerId === null || isSnapshotPlayer(snapshotPlayersOrNone(), playerId)) return null;
  const picked = pickedPlayer(playerId);
  if (!picked) throw new Error(`Player id ${playerId} is neither in the snapshot nor a live pick`);
  return picked.name;
}

function mount(
  body: HTMLElement,
  Component: ComponentType<ProtoModuleProps>,
  playerId: PlayerId | null,
  modCfg: unknown,
  { rebuildOnRefresh, livePlayers }: Required<RegisterOptions>,
): void {
  const mod = body.closest<HTMLElement>(".placed-module");
  if (!mod) return;
  let root = roots.get(body);
  // Back from the charging screen: the merge's refresh. The widget is mounted afresh, so the
  // comparison starts from its `_`-state, as it always has on a merge (the old guard rebuilt
  // the root here too).
  if (root && swappedOut.has(body)) {
    teardownRoot(body, root);
    root = undefined;
  }
  if (!root) {
    body.textContent = ""; // drop the placeholder ("Loading module HTML…")
    root = createRoot(body);
    roots.set(body, root);
  }
  // flushSync so the commit lands NOW, not on a later tick: the legacy renderers
  // refreshModuleBody replaces were synchronous, and callers read the body right after
  // (watchControlsOverflow, the merge's burst) — a converted module honours that contract.
  const r = root;
  const count = (refreshCounts.get(body) ?? 0) + 1;
  refreshCounts.set(body, count);
  // A new key = a fresh mount that re-reads the `_`-state; the same key = a re-render.
  const key = rebuildOnRefresh ? count : undefined;
  // A live widget shows any player itself; its `playerName` is null for one the snapshot
  // lacks (it reads her by id).
  const anyPlayer = LIVE && livePlayers;
  const missingName = anyPlayer ? null : notInSnapshot(playerId);
  const widget =
    missingName !== null
      ? createElement(LiveNotInDemoData, { key, name: missingName })
      : createElement(Component, {
          key,
          mod,
          body,
          playerId,
          // Looked up only here: for a player the snapshot lacks, snapshotNameOf throws.
          // Live: always null here — every live pick is outside the snapshot, so a non-null id
          // was caught by notInSnapshot above (and the clean build drops the snapshot lookup).
          playerName: anyPlayer || LIVE ? null : snapshotNameOf(playerId),
          modCfg,
        });
  // ?live=1 only (Track 2): the shared waiting / error wrapper, so a widget whose hook now
  // fetches can show "Loading…" / Retry without changing. The snapshot site renders the
  // widget exactly as before.
  flushSync(() => r.render(LIVE ? createElement(LiveBoundary, { mod, children: widget }) : widget));
}

/** Unmount the React root on a module body (if any) and clear its content. Call
 *  before handing the body back to a legacy renderer (e.g. a module-type switch),
 *  so the legacy innerHTML doesn't clobber a live React tree. */
export function unmountReactModule(body: HTMLElement): void {
  const root = roots.get(body);
  if (root) teardownRoot(body, root);
}

export interface RegisterOptions {
  /** Rebuild the component on every refreshModuleBody instead of re-rendering it — for a
   *  module that seeds React state from its `_`-state once (useState initialisers). The
   *  legacy renderers rebuilt their innerHTML from `_`-state on EVERY call, so this matches
   *  them: a refresh after an outside `_`-state change (a comparison chip's ✕, the
   *  similarity overlay) is picked up, and a refresh loses on-screen-only state (search
   *  text, a table sort) — e.g. type in a Ranking's search, press Refresh: legacy clears it.
   *  Restores don't need it since 05i-1 (their state is on before the first render); it
   *  stays for those other refreshes (05i fork 2A — the plan expected 05i to retire it).
   *  Modules that read their `_`-state every render don't need it (and a rebuild would cut
   *  Radar's D3 update transition). */
  rebuildOnRefresh?: boolean;
  /** With ?live=1, the widget reads its player from the real data, so it can show ANY
   *  player — the bridge skips its "no data yet" message for it (T2-3). Set it in the
   *  widget's own Track 2 slice. No effect on the snapshot site. */
  livePlayers?: boolean;
}

/**
 * Register a React component for a modules.json `renderer` name, in the component
 * registry the shell's refreshModuleBody renders through.
 */
export function registerReactModule(
  rendererName: string,
  Component: ComponentType<ProtoModuleProps>,
  { rebuildOnRefresh = false, livePlayers = false }: RegisterOptions = {},
): void {
  registerRenderer(rendererName, (body, playerId, modCfg) =>
    mount(body, Component, playerId, modCfg, { rebuildOnRefresh, livePlayers }),
  );
}

let observer: MutationObserver | null = null;
/** Watch the workspace for removed modules and unmount their React roots — prevents
 *  leaked roots / dangling ResizeObservers when main.js removes a .placed-module. */
export function installReactModuleBridge(): void {
  if (observer) return;
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.removedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        const bodies = node.classList.contains("module-body")
          ? [node]
          : Array.from(node.querySelectorAll<HTMLElement>(".module-body"));
        bodies.forEach((b) => unmountReactModule(b));
      });
    }
  });
  const workspace = document.getElementById("workspace") ?? document.body;
  observer.observe(workspace, { childList: true, subtree: true });
}

/** Read a `_`-prefixed instance-state property off a module element. */
export function getModState<T = unknown>(el: HTMLElement, key: string): T | undefined {
  return (el as unknown as Record<string, T>)[key];
}

/** Write a `_`-prefixed instance-state property (so saveState/undo capture it). */
export function setModState(el: HTMLElement, key: string, value: unknown): void {
  (el as unknown as Record<string, unknown>)[key] = value;
}

/**
 * A genuine module-type switch (League Table → Squad/Match Results, etc.): the caller
 * has seeded the target's `_`-state; flip the .module-title text, then re-render.
 *
 * Every target is React, so this REUSES the existing root: refreshModuleBody → mount()
 * re-renders the new component on the same root and React reconciles (the old component's
 * effects clean up, so its sub-controls strip is removed). We deliberately do NOT unmount
 * here — unmounting a root from inside its own click handler (this runs from the old
 * module's button) is unsafe: it can defer, then a second createRoot() on the same body
 * orphans the old sub-controls strip (the two-stacked-bars bug). (A deferred unmount for a
 * legacy target went in 05j-1 — no widget has a legacy renderer any more.)
 */
export function switchModuleType(mod: HTMLElement, newTitle: string): void {
  // Through the store (05b): React renders the title, so writing its text node directly
  // would leave React holding a stale one.
  canvasApi().setModuleTitle(mod, newTitle);
  canvasApi().refreshModuleBody(mod);
}
